import Flex from "../../components/Flex";
import PsychoeducationSection from "../_shared/PsychoeducationSection";
import { NestedText, SubSection } from "../_shared/TherapyBlocks";
import AllergiesBlock from "../_shared/AllergiesBlock";
import HistoryBlock from "../_shared/HistoryBlock";
import MedicationsBlock from "../_shared/MedicationsBlock";
import VitalsBlock from "../_shared/VitalsBlock";
import RosBlock from "../_shared/RosBlock";
import PhysicalExamBlock from "../_shared/PhysicalExamBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import AssessmentBlock from "../_shared/AssessmentBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import CptCodesBlock from "../_shared/CptCodesBlock";
import {
  hasAssessmentContent,
  hasCptCodes,
  hasObjectiveContent,
  hasPlanContent,
  hasSubjectiveContent,
} from "../_shared/section-content";
import {
  CarePlanTemplateList,
  KeyValPair,
  OrderList,
  RenderTextWithContainerPattern,
  ReportSection,
} from "../constant";

interface CollaborativeCareModelTemplateProps {
  data: any;
}

// Maps the saved Homicidal Ideation enum to the dropdown's display label (ECRH-5049)
const HOMICIDAL_IDEATION_LABELS: Record<string, string> = {
  NonReported: "Non-Reported",
  PresentAssessd: "Present-Assessd",
};

const CollaborativeCareModelTemplate = ({
  data,
}: CollaborativeCareModelTemplateProps) => {
  const hasBHDx = !!data?.secondaryDiagnoses;
  const hasTarget =
    data?.targetSymptom ||
    data?.measurementTool ||
    data?.treatmentGoal ||
    data?.currentProgress;
  const hasMeds =
    data?.currentPsychiatricMedications ||
    data?.medicationChangesThisVisit ||
    data?.sideEffectsReported ||
    data?.adherenceIssuesOrBarriers;
  const hasInterventions =
    data?.behavioralActivation ||
    data?.psychoeducationProvided ||
    data?.problemSolving ||
    data?.relapsePrevention;
  const hasCoord =
    data?.pcpCommunication ||
    data?.specialtyReferrals ||
    data?.communityResources ||
    data?.familyCaregiverInvolvement;
  const hasSafety =
    data?.suicidalIdeation ||
    data?.homicidalIdeation ||
    data?.overallRiskLevel ||
    data?.safetyPlanStatus;
  const hasPlan =
    data?.treatmentPlanChanges ||
    data?.patientResponseToTreatment ||
    data?.barriersToTreatment ||
    data?.nextStepsAndActionItems;
  // Reason/urgency are gated on the checkbox below, so they can no longer make
  // this section render on their own.
  const hasConsult = data?.psychiatricConsultationNeeded !== undefined;

  const hasCocm =
    hasBHDx ||
    hasTarget ||
    hasMeds ||
    hasInterventions ||
    hasCoord ||
    hasSafety ||
    hasPlan ||
    hasConsult;

  // The CoCM form captures none of the SOAP free notes or clinical blocks, so
  // these bands would otherwise print as headers with nothing under them.
  // ICD/CPT stay under Assessment/Plan — that mapping is the intended behavior.
  return (
    <>
      {/* ── Subjective ── */}
      {hasSubjectiveContent(data) && (
        <ReportSection label="Subjective">
          <RenderTextWithContainerPattern
            label="History of Present Illness"
            val={data?.historyOfPresentIllness}
          />
          <RenderTextWithContainerPattern
            label="Subjective"
            val={data?.subjectiveFreeNote}
          />
          <AllergiesBlock data={data} />
          <HistoryBlock data={data} />
          <RenderTextWithContainerPattern label="Habits" val={data?.habits} />
          <MedicationsBlock data={data} />
        </ReportSection>
      )}

      {/* ── Objective ── */}
      {hasObjectiveContent(data) && (
        <ReportSection label="Objective">
          <RenderTextWithContainerPattern
            label="Objective"
            val={data?.objectiveFreeNote}
          />
          <VitalsBlock data={data} />
          <PhysicalExamBlock data={data} />
          <RosBlock data={data} />
          <ScreeningAssessmentBlock data={data} />
        </ReportSection>
      )}

      {/* ── Assessment ── */}
      {hasAssessmentContent(data) && (
        <ReportSection label="Assessment">
          <AssessmentBlock data={data} />
        </ReportSection>
      )}

      {/* ── Plan ── */}
      {(hasPlanContent(data) || hasCptCodes(data)) && (
        <ReportSection label="Plan">
          <RenderTextWithContainerPattern
            label="Plan"
            val={data?.planFreeNote}
          />
          <PrescriptionsBlock data={data} />
          <VaccinesBlock data={data} />
          <OrderList orders={data?.patientOrders} />
          <CptCodesBlock data={data} />
          <RenderTextWithContainerPattern
            label="Care Plan"
            val={data?.carePlan}
          />
          <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
          <RenderTextWithContainerPattern
            label="Instructions"
            val={data?.instruction}
          />
        </ReportSection>
      )}

      {/* ── Collaborative Care Model Specific ── */}
      {hasCocm && (
        <ReportSection label="Collaborative Care Model">
          <Flex flexDirection={"column"} gap={1} mt={1} p={1}>
            {hasBHDx && (
              <SubSection label="Behavioral Health Diagnosis">
                <NestedText
                  label="Secondary Diagnoses"
                  val={data?.secondaryDiagnoses}
                />
              </SubSection>
            )}

            {hasTarget && (
              <SubSection label="Treatment to Target">
                {data?.targetSymptom && (
                  <KeyValPair k="Target Symptom" v={data.targetSymptom} />
                )}
                {data?.measurementTool && (
                  <KeyValPair k="Measurement Tool" v={data.measurementTool} />
                )}
                <NestedText label="Treatment Goal" val={data?.treatmentGoal} />
                <NestedText
                  label="Current Progress Toward Goal"
                  val={data?.currentProgress}
                />
              </SubSection>
            )}

            {hasMeds && (
              <SubSection label="Medication Management">
                <NestedText
                  label="Current Psychiatric Medications"
                  val={data?.currentPsychiatricMedications}
                />
                <NestedText
                  label="Medication Changes This Visit"
                  val={data?.medicationChangesThisVisit}
                />
                <NestedText
                  label="Side Effects Reported"
                  val={data?.sideEffectsReported}
                />
                <NestedText
                  label="Adherence Issues or Barriers"
                  val={data?.adherenceIssuesOrBarriers}
                />
              </SubSection>
            )}

            {hasInterventions && (
              <SubSection label="Brief Behavioral Interventions">
                <NestedText
                  label="Behavioral Activation"
                  val={data?.behavioralActivation}
                />
                <NestedText
                  label="Psychoeducation Provided"
                  val={data?.psychoeducationProvided}
                />
                <NestedText
                  label="Problem Solving"
                  val={data?.problemSolving}
                />
                <NestedText
                  label="Relapse Prevention"
                  val={data?.relapsePrevention}
                />
              </SubSection>
            )}

            {hasCoord && (
              <SubSection label="Care Coordination Activities">
                <NestedText
                  label="PCP Communication"
                  val={data?.pcpCommunication}
                />
                <NestedText
                  label="Specialty Referrals"
                  val={data?.specialtyReferrals}
                />
                <NestedText
                  label="Community Resources"
                  val={data?.communityResources}
                />
                <NestedText
                  label="Family/Caregiver Involvement"
                  val={data?.familyCaregiverInvolvement}
                />
              </SubSection>
            )}

            {hasSafety && (
              <SubSection label="Safety Assessment">
                {data?.suicidalIdeation && (
                  <KeyValPair k="Suicidal Ideation" v={data.suicidalIdeation} />
                )}
                {data?.homicidalIdeation && (
                  <KeyValPair
                    k="Homicidal Ideation"
                    v={
                      HOMICIDAL_IDEATION_LABELS[data.homicidalIdeation] ??
                      data.homicidalIdeation
                    }
                  />
                )}
                {data?.overallRiskLevel && (
                  <KeyValPair
                    k="Overall Risk Level"
                    v={data.overallRiskLevel}
                  />
                )}
                {data?.safetyPlanStatus && (
                  <KeyValPair
                    k="Safety Plan Status"
                    v={data.safetyPlanStatus}
                  />
                )}
              </SubSection>
            )}

            {hasPlan && (
              <SubSection label="Treatment Plan & Next Steps">
                <NestedText
                  label="Treatment Plan Changes"
                  val={data?.treatmentPlanChanges}
                />
                <NestedText
                  label="Patient Response to Treatment"
                  val={data?.patientResponseToTreatment}
                />
                <NestedText
                  label="Barriers to Treatment"
                  val={data?.barriersToTreatment}
                />
                <NestedText
                  label="Next Steps & Action Items"
                  val={data?.nextStepsAndActionItems}
                />
              </SubSection>
            )}

            {hasConsult && (
              <SubSection label="Psychiatric Consultation">
                {data?.psychiatricConsultationNeeded !== undefined && (
                  <KeyValPair
                    k="Psychiatric Consultation Needed"
                    v={data.psychiatricConsultationNeeded ? "Yes" : "No"}
                  />
                )}
                {/* Reason & urgency only apply when a consultation is needed —
                guard them so notes saved before the form cleared these on
                uncheck don't print stale values. */}
                {!!data?.psychiatricConsultationNeeded && (
                  <>
                    <NestedText
                      label="Reason for Consultation"
                      val={data?.reasonForConsultation}
                    />
                    {data?.urgencyLevel && (
                      <KeyValPair k="Urgency Level" v={data.urgencyLevel} />
                    )}
                  </>
                )}
              </SubSection>
            )}
          </Flex>
        </ReportSection>
      )}

      {/* ── Follow-Up ── */}
      <ReportSection label="Follow-Up">
        <RenderTextWithContainerPattern
          label="Follow Up"
          val={data?.followUp}
        />

        {/* ── Psychoeducation Session (shown below Follow Up only when required) ── */}
        <PsychoeducationSection data={data} />
      </ReportSection>
    </>
  );
};

export default CollaborativeCareModelTemplate;
